import { detectCountryCode } from "@/lib/currency";
import { standaloneApiRequest } from "@/lib/standaloneApi";

export const STANDALONE_MODE = process.env.REACT_APP_STANDALONE?.trim().toLowerCase() === "true";
const FRONTEND_PREVIEW_KEY = "hm_frontend_preview";
const FRONTEND_PREVIEW_ALLOWED = STANDALONE_MODE || process.env.NODE_ENV !== "production";

export function isFrontendWinterArcPreview() {
  if (!FRONTEND_PREVIEW_ALLOWED) return false;
  try { return localStorage.getItem(FRONTEND_PREVIEW_KEY) === "winter_arc"; } catch { return false; }
}

export function enableFrontendWinterArcPreview() {
  if (!FRONTEND_PREVIEW_ALLOWED) throw new Error("Frontend preview is disabled in production.");
  localStorage.setItem(FRONTEND_PREVIEW_KEY, "winter_arc");
}

export function normalizeApiBaseUrl(value) {
  const url = (value?.trim() || "http://localhost:8000").replace(/\/+$/, "");
  return url.endsWith("/api") ? url : `${url}/api`;
}

export const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

// Cohort covers come back as API-relative paths (/cohorts/<id>/image?v=…).
export const apiAssetUrl = (path) => (!path ? null : /^(https?:|data:|blob:)/.test(path) ? path : `${API_BASE_URL}${path}`);
const TOKEN_KEY = "hm_access_token";

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAccessToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

async function parseResponse(response) {
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (response.ok) return data;

  const detail = data?.detail;
  const message = Array.isArray(detail)
    ? detail.map((item) => item.msg || item).join(", ")
    : (typeof detail === "object" ? detail?.message : detail) || data?.message || "Something went wrong.";
  const error = new Error(message);
  error.status = response.status;
  error.data = data;
  error.code = typeof detail === "object" ? detail?.code : undefined;
  throw error;
}

export async function apiRequest(path, { method = "GET", body, auth = true, headers = {} } = {}) {
  if (STANDALONE_MODE || isFrontendWinterArcPreview()) return standaloneApiRequest(path, { method, body, auth, headers });
  const token = getAccessToken();
  const requestHeaders = { ...headers };
  if (body !== undefined) requestHeaders["Content-Type"] = "application/json";
  if (auth && token) requestHeaders.Authorization = `Bearer ${token}`;

  // Lets the API know this member uses the website (features that span web and app show both halves).
  requestHeaders["X-HM-Client"] = "web";
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parseResponse(response);
}

export async function signup(payload) {
  return apiRequest("/auth/signup", { method: "POST", body: payload, auth: false });
}

export async function login(payload) {
  return apiRequest("/auth/login", { method: "POST", body: payload, auth: false });
}

export const applyCreator = (payload) => apiRequest("/creators/applications", { method: "POST", body: payload, auth: false });
export const trackCreatorVisit = (code, visitorToken) => apiRequest(`/creators/referrals/${encodeURIComponent(code)}/visit`, { method: "POST", body: { visitorToken }, auth: false });
export const claimCreatorReferral = (code) => apiRequest("/creators/referrals/claim", { method: "POST", body: { code } });
export const getCreatorDashboard = () => apiRequest("/creators/me");

export async function getDevAccounts() {
  return apiRequest("/auth/dev/accounts", { auth: false });
}

export async function getDevModeStatus() {
  return apiRequest("/auth/dev/status", { auth: false });
}

export async function loginAsDevGuest() {
  return apiRequest("/auth/dev/guest", { method: "POST", auth: false });
}

export async function loginAsDevAccount(accountKey) {
  return apiRequest(`/auth/dev/login/${encodeURIComponent(accountKey)}`, { method: "POST", auth: false });
}

export async function getMe() {
  return apiRequest("/auth/me");
}

export async function getBillingCatalog(currency) {
  const params = new URLSearchParams();
  const country = detectCountryCode();
  if (country) params.set("country", country);
  if (currency) params.set("currency", currency);
  return apiRequest(`/billing/catalog?${params.toString()}`, { auth: false });
}

export async function createPaymentOrder(productCode, currency, paymentMethod = "payment_page", discount = {}) {
  return apiRequest("/billing/orders", {
    method: "POST",
    body: { productCode, currency, paymentMethod, couponCode: discount.couponCode || undefined, offerId: discount.offerId || undefined },
  });
}

export async function getPersonalOffers() {
  return apiRequest("/billing/offers");
}

export async function getPaymentOrderStatus(localOrderId) {
  return apiRequest(`/billing/orders/${encodeURIComponent(localOrderId)}/status`);
}

export async function verifyPaymentOrder(localOrderId, payment) {
  return apiRequest(`/billing/orders/${encodeURIComponent(localOrderId)}/verify`, {
    method: "POST",
    body: {
      razorpayOrderId: payment.razorpay_order_id,
      razorpayPaymentId: payment.razorpay_payment_id,
      razorpaySignature: payment.razorpay_signature,
    },
  });
}

export async function beginOAuth(provider) {
  return apiRequest(`/auth/oauth/${provider}/authorize`, { auth: false });
}

export async function finishOAuth(provider, payload) {
  return apiRequest(`/auth/oauth/${provider}/callback`, { method: "POST", body: payload, auth: false });
}

export async function getOnboardingProfile() {
  return apiRequest("/onboarding/profile");
}

export async function getPublicProfile(identifier) {
  return apiRequest(`/profile/${encodeURIComponent(identifier)}`);
}

export async function getPublicProfileHeatmap(identifier) {
  return apiRequest(`/profile/${encodeURIComponent(identifier)}/heatmap`);
}

export async function getOwnProfileHeatmap() {
  return apiRequest("/profile/me/heatmap");
}

export async function saveOnboardingProfile(payload) {
  return apiRequest("/onboarding/profile", { method: "PUT", body: payload });
}

export async function completeOnboardingProfile(payload) {
  return apiRequest("/onboarding/profile/complete", { method: "POST", body: payload });
}

function appendParam(params, key, value) {
  if (Array.isArray(value)) {
    value.filter(Boolean).forEach((item) => params.append(key, item));
  } else if (value !== undefined && value !== null && value !== "") {
    params.set(key, value);
  }
}

export async function getDiscoverDeck(filters = {}) {
  const params = new URLSearchParams();
  appendParam(params, "limit", filters.limit || 50);
  appendParam(params, "use_profile_preferences", filters.useProfilePreferences ?? true);
  appendParam(params, "timezones", filters.timezones);
  appendParam(params, "cities", filters.cities);
  appendParam(params, "countries", filters.countries);
  appendParam(params, "genders", filters.genders);
  appendParam(params, "chronotypes", filters.chronotypes);
  appendParam(params, "commitment_durations", filters.commitmentDurations);
  appendParam(params, "partnership_intents", filters.partnershipIntents);
  appendParam(params, "subscription_tiers", filters.subscriptionTiers);
  appendParam(params, "languages", filters.languages);
  appendParam(params, "min_age", filters.minAge);
  appendParam(params, "max_age", filters.maxAge);
  appendParam(params, "min_daily_hours", filters.minDailyHours);
  appendParam(params, "max_daily_hours", filters.maxDailyHours);
  appendParam(params, "min_commitment_hours", filters.minCommitmentHours);
  appendParam(params, "max_timezone_diff", filters.maxTimezoneDiff);
  appendParam(params, "min_overlap_score", filters.minOverlapScore);
  return apiRequest(`/discover?${params.toString()}`);
}

export async function likeDiscoverProfile(identifier) {
  return apiRequest(`/discover/${identifier}/like`, { method: "POST" });
}

export async function passDiscoverProfile(identifier) {
  return apiRequest(`/discover/${identifier}/pass`, { method: "POST" });
}

export async function getDashboard() {
  return apiRequest("/dashboard");
}

export async function getLikedProfiles() {
  return apiRequest("/liked");
}

export async function likeBackProfile(identifier) {
  return apiRequest(`/liked/${identifier}/like-back`, { method: "POST" });
}

export async function passLikedProfile(identifier) {
  return apiRequest(`/liked/${identifier}/pass`, { method: "POST" });
}

// Partner invites: a code or /invite/CODE link that pairs whoever accepts it with its owner.
export const getMyInvites = () => apiRequest("/invites");
export const createInvite = (payload) => apiRequest("/invites", { method: "POST", body: payload });
export const revokeInvite = (inviteId) => apiRequest(`/invites/${encodeURIComponent(inviteId)}`, { method: "DELETE" });
export const previewInvite = (code) => apiRequest(`/invites/${encodeURIComponent(code)}/preview`, { auth: false });
export const acceptInvite = (code) => apiRequest(`/invites/${encodeURIComponent(code)}/accept`, { method: "POST" });

// Cohorts: dated groups the team fills and then pairs by hand.
export const getPublicCohorts = () => apiRequest("/cohorts/public", { auth: false });
export const getMyCohorts = () => apiRequest("/cohorts");
// mode: "match" (the team pairs you), "partner" (with partnershipId) or "friend" (returns an invite code).
export const joinCohort = (cohortId, { goalNote, mode = "match", partnershipId = null } = {}) => apiRequest(`/cohorts/${encodeURIComponent(cohortId)}/join`, { method: "POST", body: { goalNote: goalNote || null, mode, partnershipId } });
export const answerCohortPartnerRequest = (cohortId, decision) => apiRequest(`/cohorts/${encodeURIComponent(cohortId)}/partner-request/${decision}`, { method: "POST" });
export const leaveCohort = (cohortId) => apiRequest(`/cohorts/${encodeURIComponent(cohortId)}/membership`, { method: "DELETE" });

// Deleting an account: by the member (Settings), or a request from the public /delete-account page.
export const getDeletionCheck = () => apiRequest("/account/delete");
export const deleteMyAccount = ({ confirmation, password }) => apiRequest("/account/delete", { method: "POST", body: { confirmation, password: password || null } });
export const requestAccountDeletion = ({ email, reason }) => apiRequest("/account/deletion-requests", { method: "POST", body: { email, reason: reason || null }, auth: false });

export async function getMatches() {
  return apiRequest("/matches");
}

export async function markMatchSeen(matchId) {
  return apiRequest(`/matches/${matchId}/seen`, { method: "POST" });
}

export async function startPartnership(matchId) {
  return apiRequest(`/matches/${matchId}/partnership`, { method: "POST" });
}

export async function claimInactiveMatchCredit(matchId) {
  return apiRequest(`/matches/${matchId}/inactivity-claim`, { method: "POST" });
}

export async function getTasks() {
  return apiRequest("/tasks");
}

export async function getWinterArc() { return apiRequest("/winter-arc"); }
export async function setupWinterArc() { return apiRequest("/winter-arc/setup", { method: "POST" }); }
export async function linkWinterArcTask(areaKey, taskId) {
  return apiRequest(`/winter-arc/areas/${encodeURIComponent(areaKey)}/link`, { method: "PUT", body: { taskId: taskId || null } });
}
export async function createWinterArcTask(areaKey) {
  return apiRequest(`/winter-arc/areas/${encodeURIComponent(areaKey)}/create`, { method: "POST" });
}
export async function toggleWinterArcArea(areaKey) {
  return apiRequest(`/winter-arc/areas/${encodeURIComponent(areaKey)}/toggle`, { method: "POST" });
}

export async function createTask(payload) {
  return apiRequest("/tasks", { method: "POST", body: payload });
}

export async function updateTask(taskId, payload) {
  return apiRequest(`/tasks/${taskId}`, { method: "PATCH", body: payload });
}

export async function deleteTask(taskId) {
  return apiRequest(`/tasks/${taskId}`, { method: "DELETE" });
}

export async function toggleTask(taskId, date) {
  return apiRequest(`/tasks/${taskId}/toggle`, { method: "POST", body: date ? { date } : undefined });
}

export async function getWorkspace(partnershipId) {
  return apiRequest(`/workspace/${partnershipId}`);
}

export async function proposeCalendarMeet(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/calendar-meets`, { method: "POST", body: payload });
}

export async function acceptCalendarMeet(partnershipId, meetId) {
  return apiRequest(`/workspace/${partnershipId}/calendar-meets/${meetId}/accept`, { method: "POST" });
}

export async function removeCalendarMeet(partnershipId, meetId) {
  return apiRequest(`/workspace/${partnershipId}/calendar-meets/${meetId}`, { method: "DELETE" });
}

export async function submitExplorationDecision(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/exploration/decision`, { method: "POST", body: payload });
}

export async function proposeWorkspaceSprint(partnershipId, payload = {}) {
  return apiRequest(`/workspace/${partnershipId}/sprints`, { method: "POST", body: payload });
}

export async function acceptWorkspaceSprint(partnershipId, sprintId) {
  return apiRequest(`/workspace/${partnershipId}/sprints/${sprintId}/accept`, { method: "POST" });
}

export async function settleWorkspaceSprint(partnershipId, sprintId) {
  return apiRequest(`/workspace/${partnershipId}/sprints/${sprintId}/settle`, { method: "POST" });
}

export async function allocateWorkspaceSprintStake(partnershipId, sprintId) {
  return apiRequest(`/workspace/${partnershipId}/sprints/${sprintId}/stake`, { method: "POST" });
}

export async function getWallet() { return apiRequest("/wallet"); }
export async function mergeWalletEarnings(tokens) {
  return apiRequest("/wallet/earnings/merge", { method: "POST", body: { tokens } });
}
export async function redeemWalletEarnings(tokens) {
  return apiRequest("/wallet/earnings/redemptions", { method: "POST", body: { tokens } });
}
export async function claimAdReward(rewardId) { return apiRequest("/wallet/ad-rewards", { method: "POST", body: { rewardId } }); }

export async function importWorkspaceTask(partnershipId, taskId) {
  return apiRequest(`/workspace/${partnershipId}/tasks/import/${taskId}`, { method: "POST" });
}

export async function saveWorkspaceCheckin(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/checkins/today`, { method: "PUT", body: payload });
}

export async function createWorkspaceSession(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/sessions`, { method: "POST", body: payload });
}

export async function deleteWorkspaceSession(partnershipId, sessionId) {
  return apiRequest(`/workspace/${partnershipId}/sessions/${sessionId}`, { method: "DELETE" });
}

export async function logWorkspaceFocusSession(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/focus-sessions`, { method: "POST", body: payload });
}

export async function startWorkspaceFocusSession(partnershipId, durationMinutes, taskId = null, firstAction = null) {
  return apiRequest(`/workspace/${partnershipId}/focus-sessions/start`, {
    method: "POST",
    body: { durationMinutes, taskId, firstAction },
  });
}

export async function getSoloFocus() {
  return apiRequest("/focus");
}

export async function startSoloFocusSession(durationMinutes, taskId = null, firstAction = null) {
  return apiRequest("/focus/start", { method: "POST", body: { durationMinutes, taskId, firstAction } });
}

export async function updateSoloFocusSession(sessionId, action, addedMinutes) {
  return apiRequest(`/focus/${sessionId}`, { method: "PATCH", body: { action, addedMinutes } });
}

export async function updateWorkspaceFocusSession(partnershipId, sessionId, action, addedMinutes) {
  return apiRequest(`/workspace/${partnershipId}/focus-sessions/${sessionId}`, {
    method: "PATCH",
    body: { action, addedMinutes },
  });
}

export async function getWorkspaceFocusPresence(partnershipId) {
  return apiRequest(`/workspace/${partnershipId}/focus-presence`);
}

export async function createWorkspaceTask(partnershipId, payload) {
  return apiRequest(`/workspace/${partnershipId}/tasks`, { method: "POST", body: payload });
}

export async function deleteWorkspaceTask(partnershipId, taskId) {
  return apiRequest(`/workspace/${partnershipId}/tasks/${taskId}`, { method: "DELETE" });
}

export async function toggleWorkspaceTask(partnershipId, taskId, date) {
  return apiRequest(`/workspace/${partnershipId}/tasks/${taskId}/toggle`, { method: "POST", body: date ? { date } : undefined });
}

export async function verifyWorkspaceTask(partnershipId, taskId, date) {
  return apiRequest(`/workspace/${partnershipId}/tasks/${taskId}/verify`, { method: "POST", body: date ? { date } : undefined });
}

export async function nudgeWorkspaceTask(partnershipId, taskId) {
  return apiRequest(`/workspace/${partnershipId}/tasks/${taskId}/nudge`, { method: "POST" });
}

export async function getFlex(offset = 0) { return apiRequest(`/flex?focus_offset=${offset}`); }
export async function verifyFocusFlex(sessionId) { return apiRequest(`/flex/focus/${encodeURIComponent(sessionId)}/verify`, { method: "POST" }); }
export async function unlockPartnerFlexBadge(category, partnershipId) {
  return apiRequest(`/flex/${category}/partner-unlock${partnershipId ? `?partnership_id=${encodeURIComponent(partnershipId)}` : ""}`, { method: "POST" });
}

export async function getChatThreads() {
  return apiRequest("/chats");
}

export async function getChatMessages(matchId, { before, limit = 50 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set("before", before);
  return apiRequest(`/chats/${matchId}/messages?${params.toString()}`);
}

export async function sendChatMessage(matchId, body, clientMessageId) {
  return apiRequest(`/chats/${matchId}/messages`, {
    method: "POST",
    body: { body, clientMessageId },
  });
}

export async function unlockChatWithAd(matchId) {
  return apiRequest(`/chats/${matchId}/ad-unlock`, { method: "POST" });
}

export async function markChatRead(matchId) {
  return apiRequest(`/chats/${matchId}/read`, { method: "POST" });
}

// ---- Distraction shield ----
export async function getShield(day) {
  return apiRequest(`/shield?day=${day}`);
}

export async function getPartnerShield(partnershipId, day) {
  return apiRequest(`/shield/partners/${partnershipId}?day=${day}`);
}

export async function saveShieldRule(rule) {
  return apiRequest("/shield/rules", { method: "POST", body: rule });
}

export async function deleteShieldRule(ruleId) {
  return apiRequest(`/shield/rules/${ruleId}`, { method: "DELETE" });
}

export async function setShieldShare(partnershipId, level) {
  return apiRequest(`/shield/shares/${partnershipId}`, { method: "PUT", body: { level } });
}

export async function connectShieldDevice(device) {
  return apiRequest("/shield/devices", { method: "POST", body: device });
}

export async function disconnectShieldDevice(deviceId) {
  return apiRequest(`/shield/devices/${deviceId}`, { method: "DELETE" });
}

export async function getNotifications(limit = 30) {
  return apiRequest(`/notifications?limit=${limit}`);
}

export async function readNotification(notificationId) {
  return apiRequest(`/notifications/${notificationId}/read`, { method: "PATCH" });
}

export async function readAllNotifications() {
  return apiRequest("/notifications/read-all", { method: "POST" });
}

export async function clearNotification(notificationId) {
  return apiRequest(`/notifications/${notificationId}`, { method: "DELETE" });
}

export async function clearAllNotifications() {
  return apiRequest("/notifications", { method: "DELETE" });
}

export async function getNotificationPreferences() {
  return apiRequest("/notifications/preferences");
}

export async function updateNotificationPreferences(payload) {
  return apiRequest("/notifications/preferences", { method: "PUT", body: payload });
}

export async function getPushConfig() {
  return apiRequest("/notifications/push/config");
}

export async function savePushSubscription(subscription) {
  return apiRequest("/notifications/push/subscriptions", { method: "POST", body: subscription });
}

export async function deletePushSubscription(endpoint) {
  return apiRequest("/notifications/push/subscriptions", { method: "DELETE", body: { endpoint } });
}

export function getChatWebSocketUrl(matchId) {
  const token = getAccessToken();
  const apiUrl = new URL(API_BASE_URL, window.location.origin);
  const protocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
  const apiPath = apiUrl.pathname.replace(/\/+$/, "");
  return `${protocol}//${apiUrl.host}${apiPath}/chats/${matchId}/ws?token=${encodeURIComponent(token || "")}`;
}
